import {
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { z } from "zod";

import { STUDY_DIRECTIONS } from "@/features/study/directions";
import { DeckPage } from "@/routes/deck-route";
import { OverviewPage } from "@/routes/index-route";
import { NotFoundPage, RootLayout, RouteErrorPage } from "@/routes/root-layout";
import { StatsPage } from "@/routes/stats-route";
import { StudyPage } from "@/routes/study-route";

/**
 * Route shape:
 *
 *   /                    the deck grid
 *   /stats               time, answers and streaks, from local statistics
 *   /deck/$deckId        the deck's own page: word list, direction, start
 *   /deck/$deckId/study  the session
 *
 * The deck used to open in a drawer over the grid, which capped how much could
 * be shown and nested a scroll area inside the page's own. A real page has the
 * whole viewport, and "back" means what a learner expects.
 *
 * The deck id is a path param rather than a search param because it identifies
 * the resource; direction and tags stay in the search string because they are
 * options on it, and they carry through to /study so a session survives a
 * refresh and can be linked.
 */

/**
 * Tags normally travel as a JSON array (`?tags=["a","b"]`), but a hand-typed
 * `?tags=a,b` is accepted too. Anything else falls back to "all tags", which is
 * also the default: tags are a filter, not a required choice.
 */
const tagsSchema = z
  .union([z.array(z.string()), z.string().transform((raw) => raw.split(","))])
  .transform((tags) => tags.map((tag) => tag.trim()).filter(Boolean))
  .transform((tags) => (tags.length > 0 ? tags : undefined))
  .optional()
  .catch(undefined);

/**
 * Every field has its own `.catch(...)`, so a truncated or hand-edited URL
 * degrades to a typed default instead of throwing during render.
 */
const deckSearchSchema = z.object({
  tags: tagsSchema,
  /**
   * Optional, like `tags`: absent means the default direction, so a link that
   * has no opinion can omit it and the URL stays clean until the learner
   * actually flips the switch. Resolve it with `resolveDirection`.
   */
  direction: z.enum(STUDY_DIRECTIONS).optional().catch(undefined),
});

export type DeckSearch = z.output<typeof deckSearchSchema>;

const validateDeckSearch = (search: Record<string, unknown>): DeckSearch =>
  deckSearchSchema.parse(search);

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: OverviewPage,
});

/**
 * A sibling route rather than a panel on the overview, so the tab bar the two
 * share is real navigation: the back button works between them, and a glance at
 * the numbers is a link someone can keep.
 */
const statsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stats",
  component: StatsPage,
});

const deckRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/deck/$deckId",
  validateSearch: validateDeckSearch,
  component: DeckPage,
});

const studyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/deck/$deckId/study",
  validateSearch: validateDeckSearch,
  component: StudyPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  statsRoute,
  deckRoute,
  studyRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: RouteErrorPage,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
